import React, { useMemo, useState } from 'react'
import Typography from '@material-ui/core/Typography'
import Box from '@material-ui/core/Box'
import Container from '@material-ui/core/Container'
import Grid from '@material-ui/core/Grid'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useTheme } from '@material-ui/core/styles'
import { IconCheckMark } from '@components/atoms/icons'
import CategoryTabs from '@components/category-tabs'
import { PGA_COACH_PERSONAS } from '@lib/pga-coach/constants'
import useStyles from './coach-experiences.styles'

/** Maps journey persona ids → coach profile `experienceTypes.typeName` values (null = all types). */
const PERSONA_TAB_EXPERIENCE_TYPE_NAMES = {
  'start-golf': ['Getting Started'],
  'play-better': ['Improving Your Game'],
  'break-100': ['Improving Your Game'],
  juniors: ['Getting Started'],
  'womens-golf': ['Having More Fun'],
  'military-veterans': null,
}

function hasLessonServices(serviceTypes) {
  return Array.isArray(serviceTypes) && serviceTypes.some((st) => (st.services || []).length > 0)
}

function getLessonChecklistRows(serviceTypes) {
  if (!Array.isArray(serviceTypes)) return []
  return serviceTypes.flatMap((st) => (st.services || []).map((svc) => ({
    key: `${st.typeName}::${svc.serviceName}`,
    text: svc.serviceName,
  })))
}

function coachExperiencesHasVisibleContent({
  experiences,
  serviceTypes,
  skills,
}) {
  const hasExperiences = Array.isArray(experiences) && experiences.length > 0
  const hasLessons = hasLessonServices(serviceTypes)
  const hasSkills = Array.isArray(skills) && skills.length > 0
  return hasExperiences || hasLessons || hasSkills
}

function getExperiencesForPersona(personaId, experiences) {
  const list = experiences || []
  const allowed = PERSONA_TAB_EXPERIENCE_TYPE_NAMES[personaId]
  if (allowed == null) {
    return list
  }
  const allowedSet = new Set(allowed)
  return list.filter((row) => allowedSet.has(row.typeName))
}

/**
 * Flat list: lessons + skills (every persona), then CMS rows filtered by persona.
 * Follower/student counts are shown in the coach bio, not duplicated here.
 */
function buildPersonaCombinedItems(personaId, {
  experiences,
  serviceTypes,
  skills,
}) {
  const items = []

  getLessonChecklistRows(serviceTypes).forEach((row) => {
    items.push({ kind: 'check', key: `lesson-${row.key}`, text: row.text })
  });
  (skills || []).forEach((s) => {
    items.push({ kind: 'check', key: `skill-${s.skillName}`, text: s.skillName })
  })

  const filteredExperiences = getExperiencesForPersona(personaId, experiences)
  const cmsGroupsWithRows = filteredExperiences.filter((exp) => (exp.experiences || []).length > 0)
  const showCmsTypeLabels = cmsGroupsWithRows.length > 1

  cmsGroupsWithRows.forEach((exp) => {
    if (showCmsTypeLabels) {
      items.push({ kind: 'heading', key: `cms-h-${exp.typeName}`, text: exp.typeName })
    }
    (exp.experiences || []).forEach((v) => {
      items.push({
        kind: 'check',
        key: `cms-${exp.typeName}::${v.experienceName}`,
        text: v.experienceName,
      })
    })
  })

  return items
}

/** Rows shown for a persona tab (lessons + skills + CMS experiences filtered by persona). */
export function countCoachPersonaChecklistItems(personaId, coach) {
  return buildPersonaCombinedItems(personaId, {
    experiences: coach?.experienceTypes,
    serviceTypes: coach?.serviceTypes,
    skills: coach?.skills,
  }).filter((item) => item.kind === 'check').length
}

function CoachExperiences(props) {
  const {
    firstName,
    experiences = [],
    serviceTypes = [],
    skills = [],
  } = props
  const styles = useStyles()
  const theme = useTheme()
  const isDensePersonaTabs = useMediaQuery(theme.breakpoints.down('sm'))
  const [personaTab, setPersonaTab] = useState('start-golf')

  const personaTabItems = useMemo(
    () => PGA_COACH_PERSONAS.map((persona) => ({ id: persona.id, label: persona.label })),
    [],
  )

  const combinedItems = useMemo(
    () => buildPersonaCombinedItems(personaTab, {
      experiences,
      serviceTypes,
      skills,
    }),
    [personaTab, experiences, serviceTypes, skills],
  )

  const hasCheckRows = useMemo(
    () => combinedItems.some((item) => item.kind === 'check'),
    [combinedItems],
  )

  const hasContent = coachExperiencesHasVisibleContent({
    experiences,
    serviceTypes,
    skills,
  })

  return hasContent ? (
    <Box className={styles.root}>
      <Container maxWidth={false} className={styles.container}>
        <Grid container justifyContent="center">
          <Grid item xs={12} md={10} lg={11}>
            <Typography variant="overline" component="p" className={styles.title}>
              {firstName && `${firstName}'s Experience`}
            </Typography>
            <Typography variant="h3" component="h2" className={styles.headline}>
              Helping Golfers Achieve Their Goals
            </Typography>
            <Box className={styles.personaTabsSection}>
              <CategoryTabs
                className={styles.personaTabsRoot}
                dense={isDensePersonaTabs}
                items={personaTabItems}
                value={personaTab}
                onChange={setPersonaTab}
                ariaLabel="PGA Coach journey personas"
              />
              <hr className={styles.tabsDivider} />
            </Box>
            {
              !hasCheckRows ? (
                <Typography variant="body2" className={styles.emptyPersonaMessage}>
                  This coach has not listed experiences for this golfer goal yet.
                </Typography>
              ) : (
                <Box className={styles.experienceWrapper}>
                  <Box className={styles.experienceType}>
                    {
                      combinedItems.map((item, index) => {
                        if (item.kind === 'heading') {
                          return (
                            <Box
                              key={item.key}
                              className={`${styles.experienceSectionHeading} ${index > 0 ? styles.experienceSectionHeadingSpaced : ''}`}
                            >
                              <Typography variant="overline" component="p" className={styles.experienceSectionLabel}>
                                {item.text}
                              </Typography>
                            </Box>
                          )
                        }
                        return (
                          <Box key={item.key} className={styles.experienceEntry}>
                            <IconCheckMark
                              className={styles.checkIcon}
                              fill={theme.palette.gold.main}
                            />
                            <Typography variant="body2" className={styles.experienceName}>{item.text}</Typography>
                          </Box>
                        )
                      })
                    }
                  </Box>
                </Box>
              )
            }
          </Grid>
        </Grid>
      </Container>
    </Box>
  ) : null
}

CoachExperiences.defaultProps = {
  experiences: [],
  serviceTypes: [],
  skills: [],
}

export default CoachExperiences
